import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import intl from 'react-intl-universal'
import { LOCALE } from '@foxrenderfarm/internals'
import { useClickAway } from '../../../../hooks'
import { baseTaskUrl, getRegisterLink } from '../../../../utils'
import { CloseIcon, MenuIcon } from '../../svg'
import {
  MobileNavAuthButton,
  MobileNavAuthButtons,
  MobileNavDivider,
  MobileNavPanel,
  MobileNavRoot,
  MobileNavMenuButton,
  MobileNavScroll,
  MobileNavBackdrop,
} from './atoms'
import { MobileNavAccordion } from './mobile-nav-accordion'
import { AboutPanel } from './panels/about-panel'
import { ProductsPanel } from './panels/products-panel'
import { PricingPanel } from './panels/pricing-panel'
import { ResourcesPanel } from './panels/resources-panel'
import { SolutionsPanel } from './panels/solutions-panel'
import { SupportPanel } from './panels/support-panel'

interface MobileNavProps {
  currentLocale: LOCALE
  currentPathname: string
  origin: string
}

export const MobileNav = ({ currentLocale, currentPathname, origin }: MobileNavProps) => {
  const [open, setOpen] = useState(false)
  const [supportOpen, setSupportOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement | null>(null)

  useClickAway(rootRef, () => {
    if (open) {
      setOpen(false)
    }
  })

  useEffect(() => {
    if (typeof document === 'undefined') {
      return undefined
    }

    document.body.style.overflow = open ? 'hidden' : 'auto'
    return () => {
      document.body.style.overflow = 'auto'
    }
  }, [open])

  useEffect(() => {
    if (!open) {
      setSupportOpen(false)
    }
  }, [open])

  return (
    <MobileNavRoot ref={rootRef}>
      <MobileNavMenuButton
        type='button'
        aria-label={open ? 'Close menu' : 'Open menu'}
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        {open ? <CloseIcon /> : <MenuIcon />}
      </MobileNavMenuButton>
      {open && (
        <>
          {typeof document !== 'undefined' &&
            createPortal(
              <MobileNavBackdrop aria-hidden='true' onClick={() => setOpen(false)} />,
              document.body,
            )}
          <MobileNavPanel aria-label='Mobile navigation'>
            <MobileNavScroll>
              <MobileNavAccordion title={intl.get('index.header.products')}>
                <ProductsPanel
                  currentLocale={currentLocale}
                  currentPathname={currentPathname}
                  origin={origin}
                />
              </MobileNavAccordion>
              <MobileNavDivider />

              <MobileNavAccordion title={intl.get('index.header.solutions')}>
                <SolutionsPanel
                  currentLocale={currentLocale}
                  currentPathname={currentPathname}
                  origin={origin}
                />
              </MobileNavAccordion>
              <MobileNavDivider />

              <MobileNavAccordion title={intl.get('index.header.pricing')}>
                <PricingPanel
                  currentLocale={currentLocale}
                  currentPathname={currentPathname}
                  origin={origin}
                  onNavigate={() => setOpen(false)}
                />
              </MobileNavAccordion>
              <MobileNavDivider />

              <MobileNavAccordion title={intl.get('index.header.resources')}>
                <ResourcesPanel currentLocale={currentLocale} origin={origin} />
              </MobileNavAccordion>
              <MobileNavDivider />

              <MobileNavAccordion
                title={intl.get('index.header.support')}
                onOpenChange={setSupportOpen}
              >
                <SupportPanel currentLocale={currentLocale} origin={origin} />
              </MobileNavAccordion>
              {!supportOpen && <MobileNavDivider />}

              <MobileNavAccordion title={intl.get('index.header.about')}>
                <AboutPanel
                  currentLocale={currentLocale}
                  currentPathname={currentPathname}
                  origin={origin}
                />
              </MobileNavAccordion>
              <MobileNavDivider />
            </MobileNavScroll>

            <MobileNavAuthButtons>
              <MobileNavAuthButton href={`${baseTaskUrl()}/sso/signin`}>
                {intl.get('index.header.signIn')}
              </MobileNavAuthButton>
              <MobileNavAuthButton $variant='register' href={getRegisterLink()}>
                {intl.get('index.header.register')}
              </MobileNavAuthButton>
            </MobileNavAuthButtons>
          </MobileNavPanel>
        </>
      )}
    </MobileNavRoot>
  )
}
